import type { Pricing } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { localeNames, locales } from '../i18n';
import { Arrow, Check, Mark, Office, Plus, User } from '../components/Icons';

/** Udział przychodu, powyżej którego opłata nie rośnie — z cennika, jako liczba procent. */
export function capPercent(pricing: Pricing | null): number {
  return Math.round(Number(pricing?.revenueShareCap ?? 0.2) * 100);
}

/** Ścieżka wejścia — ustawia pierwszy ekran po zalogowaniu i kolejność pytań w rejestracji. */
export type Path = 'company' | 'office' | 'founding';

/**
 * Ekran startowy (projekt „Start, logowanie, rejestracja”, ekran 01).
 *
 * Trzy ścieżki, nie trzy plany: wybór niczego nie blokuje ani nie kosztuje. Dlatego nie pytamy
 * o niego po zalogowaniu — kto się pomyli, zmieni to później bez żadnych konsekwencji.
 */
export function Start({
  t,
  locale,
  path,
  pricing,
  onPath,
  onLocale,
  onRegister,
  onSignIn,
}: {
  t: Dictionary;
  locale: Locale;
  path: Path;
  pricing: Pricing | null;
  onPath: (path: Path) => void;
  onLocale: (locale: Locale) => void;
  onRegister: () => void;
  onSignIn: () => void;
}) {
  const paths: { id: Path; icon: React.ReactNode }[] = [
    { id: 'company', icon: <User /> },
    { id: 'office', icon: <Office /> },
    { id: 'founding', icon: <Plus /> },
  ];

  return (
    <div className="frame">
      <div className="side">
        <div className="brand">
          <Mark white />
          <b>{t.appName}</b>
        </div>
        <div>
          <div className="claim">{t.start.claim}</div>
          <p className="lead">{t.start.lead}</p>
        </div>
        <ul>
          {/* Obietnice niosą liczby z cennika, a nie przepisane — po zmianie cennika
              nie zostanie tu stara wartość */}
          {t.start.promises(pricing?.trialMonths ?? 3, capPercent(pricing)).map((promise) => (
            <li key={promise}>
              <Check />
              {promise}
            </li>
          ))}
        </ul>
        <div className="side-foot">
          <span>{t.start.euServers}</span>
          <span>{locales.map((code) => localeNames[code]).join(' · ')}</span>
        </div>
      </div>

      <div className="pane">
        <div className="top">
          <span className="kicker">{t.start.kicker}</span>
          <div className="langsel" role="group" aria-label={t.language}>
            {locales.map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={code === locale}
                onClick={() => onLocale(code)}
              >
                {localeNames[code]}
              </button>
            ))}
          </div>
        </div>

        <div className="hd">
          <h3>{t.start.whoAreYou}</h3>
          <p>{t.start.whoAreYouLead}</p>
        </div>

        <div className="picks">
          {paths.map(({ id, icon }) => (
            <button
              key={id}
              type="button"
              className="pick"
              aria-pressed={path === id}
              onClick={() => onPath(id)}
            >
              <span className="ic">{icon}</span>
              <span className="tx">
                <b>{t.start.paths[id].title}</b>
                <span>{t.start.paths[id].lead}</span>
              </span>
              <span className="go">
                <Arrow />
              </span>
            </button>
          ))}
        </div>

        <div className="row">
          <button type="button" className="btn" onClick={onRegister}>
            {t.start.createAccount}
            <Arrow />
          </button>
          <button type="button" className="btn secondary" onClick={onSignIn}>
            {t.start.haveAccount}
          </button>
        </div>

        <div className="entry-foot">{t.start.invitationHint}</div>
      </div>
    </div>
  );
}
